<template>
  <b-layout :colors="$site.colors" :header-title="$t('pages.gift.pass.confirm.title')" :header-back-route="{ path: '/gift/pass/customer', query: $store.getters['gift/urlQuery'] }" :breadcrumbs="breadcrumbs">
    <b-container class="mt-12">
      <b-summary>
        <template #summary>
          <gift-pass-detail />
        </template>

        <div>
          <b-alert show variant="warning">
            <h4 class="alert-heading">
              {{ $t('pages.gift.pass.confirm.title') }}
            </h4>
            <p>{{ $t('pages.gift.pass.confirm.almostDone') }}</p>
          </b-alert>

          <b-errors :errors="errors" />

          <b-card :title="$t('pages.gift.pass.confirm.giftDetails')" class="mb-8">
            <div class="confirmation-details--customer text-sm text-secondary ">
              <strong>{{ $t('general.purchaser') }}:</strong> {{ customer.displayName }} ({{ customer.email }})
            </div>

            <div class="confirmation-details--services text-sm text-secondary ">
              <strong>{{ $t('general.recipient') }}:</strong> {{ to.name }} ({{ to.email }})
            </div>

            <div v-if="deliveryDate" class="confirmation-details--date text-sm text-secondary ">
              <strong>{{ $t('general.deliveryDate') }}:</strong> {{ $format.date(deliveryDate.getTime() / 1000, 'local') }}
            </div>

            <div class="confirmation-details--services text-sm text-secondary ">
              <strong>{{ $t('general.price') }}:</strong> {{ pass.priceLabel }}
            </div>
          </b-card>

          <b-card
            v-if="response && response.terms.required"
            ref="terms"
            :title="response.terms.title"
            class="mb-8"
          >
            <b-html :html="response.terms.content" class="mb-4 text-sm text-secondary" />

            <BCheckbox v-model="confirmData.termsAccepted" :label="response.terms.acceptLabel" />
          </b-card>

          <div v-if="loading">
            <b-card class="mb-3 text-center">
              <b-spinner label="Loading" type="grow" class="m-2 bookapp-action-tint-color" />
            </b-card>
          </div>

          <div v-if="response && (!response.payment || !response.payment.methods)">
            <b-button
              type="button"
              class="w-full mt-4"
              variant="action"
              :loading="confirming"
              :disabled="confirming"
              @click="confirm"
            >
              {{ $t('general.confirm') }} »
            </b-button>
          </div>

          <b-card
            v-if="response && response.payment && response.payment.methods"
            :title="$t('general.payment')"
            :subtitle="response.payment.priceLabel"
            content-class=""
            class="mb-3"
          >
            <div>
              <PaymentForm
                :confirm-button-title="$t('general.buyNow')"
                :methods="response.payment.methods"
                :label="pass.name"
                :amount="response.payment.price"
                :amount-label="response.payment.priceLabel"
                :customer="customer"
                :fallback-route="externalFallbackRouteCallback"
                :action="actionCallback"
                :processing="processingPayment"
                :disabled="!isValid"
                @callback="paymentCallback"
              />
            </div>
          </b-card>
        </div>
      </b-summary>
    </b-container>
  </b-layout>
</template>

<script>
import GiftPassDetail from '@/components/gift/elements/PassDetail.vue'
import PaymentForm from '@/components/payments/PaymentForm'
import Utils from '@/utils'

definePageMeta({
  tabs: ['giftCard']
});

export default defineNuxtComponent({
  components: {
    GiftPassDetail,
    PaymentForm
  },
  data () {
    return {
      errors: [],
      loading: false,
      response: null,
      processingPayment: false,
      confirming: false,
      confirmData: {
        termsAccepted: false,
        payment: {
          data: null,
          params: null
        }
      }
    }
  },
  async asyncData () {
    const route = useRoute()
    const giftStore = useGiftStore()
    
    await giftStore.setFromUrlQuery(route.query)

    return {}
  },
  computed: {
    breadcrumbs () {
      return [
        { text: this.$site.tabs.getName('giftCard', 'Gift Card'), to: { path: '/gift' } },
        { text: this.pass.name, to: { path: '/gift/pass', query: { id: this.pass.id } } },
        { text: this.$t('pages.gift.pass.customer.title'), to: { path: '/gift/pass/customer', query: { ...this.$store.getters['gift/urlQuery'], override: true } } },
        { text: this.$t('pages.gift.pass.confirm.title'), active: true }
      ]
    },
    pass () {
      return this.$store.getters['gift/pass']
    },
    to () {
      return this.$store.getters['gift/to']
    },
    from () {
      return this.$store.getters['gift/from']
    },
    message () {
      return this.$store.getters['gift/message']
    },
    deliveryDate () {
      return this.$store.getters['gift/deliveryDate']
    },
    customer () {
      return this.$store.getters['gift/customer']
    },
    isValid () {
      return (!this.response.terms.required || (this.response.terms.required && this.confirmData.termsAccepted))
    }
  },
  created () {
    if (!this.pass) {
      this.$router.push({ path: '/gift' })
    }
  },
  mounted () {
    this.confirmData.termsAccepted = (parseInt(this.$route.query.terms) === 1)

    this.loadData()
  },
  methods: {
    formatHtml (text) {
      return Utils.text.formatHtml(text)
    },
    async loadData () {
      this.loading = true

      try {
        const response = await this.$api.pass.purchase(this.$api.ACTION_INFO, this.customer, this.pass, this.confirmData.termsAccepted, null, null, null, null, { to: this.to, from: this.from, message: this.message, deliveryDate: this.deliveryDate })

        if (response.success) {
          this.response = response
        } else if (response.errors) {
          this.errors = response.errors
        }
      } catch (error) {
        // this.$sentry.captureException(error)
        this.errors = [this.$t('general.errorRequest')]
      }

      this.loading = false
    },
    async confirm () {
      if (this.response.terms.required && !this.confirmData.termsAccepted) {
        this.errors = ['You must accept ' + this.response.terms.title]

        this.$nextTick(() => {
          // this.scrollTo(this.$refs.terms)
          this.$refs?.terms?.$el?.scrollIntoView({ behavior: 'smooth' })
        })

        return false
      }

      this.confirming = true

      try {
        const response = await this.$api.pass.purchase(this.$api.ACTION_CONFIRM, this.customer, this.pass, this.confirmData.termsAccepted, null, null, null, null, { to: this.to, from: this.from, message: this.message, deliveryDate: this.deliveryDate })

        if (response.success) {
          const customerPass = response.customerPass

          this.$router.push({ path: '/gift/pass/complete', query: { id: customerPass.id, signature: customerPass.signature } })
        } else if (response.errors) {
          this.errors = response.errors
        }
      } catch (error) {
        // this.$sentry.captureException(error)
        this.errors = [this.$t('general.errorRequest')]
      }

      this.confirming = false
    },
    externalFallbackRouteCallback (method) {
      const urlQuery = this.$store.getters['gift/urlQuery']

      urlQuery.terms = 1

      return {
        path: '/gift/pass/confirm',
        query: urlQuery
      }
    },
    actionCallback (method, params) {
      return this.$api.pass.purchase(this.$api.ACTION_INTEGRATION, this.customer, this.pass, this.confirmData.termsAccepted, null, null, method.data, params, { to: this.to, from: this.from, message: this.message, deliveryDate: this.deliveryDate })
    },
    async paymentCallback (method, { success, params, errors }) {
      if (success) {
        this.processingPayment = true
        const response = await this.$api.pass.purchase(this.$api.ACTION_CONFIRM, this.customer, this.pass, this.confirmData.termsAccepted, null, null, method.data, params, { to: this.to, from: this.from, message: this.message, deliveryDate: this.deliveryDate })

        if (response.success) {
          const customerPass = response.customerPass

          this.$router.push({ path: '/gift/pass/complete', query: { id: customerPass.id, signature: customerPass.signature } })
        } else {
          this.processingPayment = false
          this.errors = response.errors
        }
      } else {
        this.errors = errors
      }
    }
  }
})
</script>
